import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { appEnv } from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: appEnv.cognitoUserPoolId,
  ClientId: appEnv.cognitoClientId,
});

export interface MfaChallenge {
  cognitoUser: CognitoUser;
}

export type SignInResult =
  | { status: 'success'; session: CognitoUserSession }
  | { status: 'mfa_required'; challenge: MfaChallenge }
  | { status: 'new_password_required'; cognitoUser: CognitoUser };

/**
 * Starts a USER_SRP_AUTH sign-in. MFA is OPTIONAL per admin user (see
 * infra/lib/auth-stack.ts, cognito.Mfa.OPTIONAL) - users who have enrolled
 * a TOTP device get an MFA challenge after a successful password check;
 * users who haven't go straight to a session. Callers must handle both.
 */
export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ status: 'success', session }),
      onFailure: (err) => reject(err),
      totpRequired: () => resolve({ status: 'mfa_required', challenge: { cognitoUser } }),
      mfaRequired: () => resolve({ status: 'mfa_required', challenge: { cognitoUser } }),
      newPasswordRequired: () => resolve({ status: 'new_password_required', cognitoUser }),
    });
  });
}

export function submitMfaCode(challenge: MfaChallenge, code: string): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    challenge.cognitoUser.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
      },
      'SOFTWARE_TOKEN_MFA',
    );
  });
}

export function completeNewPassword(cognitoUser: CognitoUser, newPassword: string): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
        totpRequired: () => reject(new Error('MFA setup is required before sign-in can complete.')),
      },
    );
  });
}

/** Returns the current session if one exists and is valid, refreshing tokens if needed. */
export function getCurrentSession(): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        resolve(null);
        return;
      }
      resolve(session);
    });
  });
}

export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser();
  cognitoUser?.signOut();
}

export function getAccessToken(session: CognitoUserSession): string {
  return session.getAccessToken().getJwtToken();
}

export function getIdToken(session: CognitoUserSession): string {
  return session.getIdToken().getJwtToken();
}

export function getGroupsFromSession(session: CognitoUserSession): string[] {
  const groups = session.getIdToken().payload['cognito:groups'];
  return Array.isArray(groups) ? groups : [];
}

export function getEmailFromSession(session: CognitoUserSession): string | undefined {
  const email = session.getIdToken().payload.email;
  return typeof email === 'string' ? email : undefined;
}

/**
 * Starts the self-service "forgot password" flow: Cognito emails a
 * verification code to the account's email address (accountRecovery is
 * EMAIL_ONLY - see infra/lib/auth-stack.ts). Resolves even if the email
 * doesn't correspond to a real account, since Cognito itself does not
 * distinguish "unknown user" from "code sent" in forgotPassword's
 * onSuccess/onFailure callbacks for this flow by design (CodeDeliveryDetails
 * is only present when a real account exists) - callers must not use this
 * to infer whether an email is registered.
 */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Completes the forgot-password flow using the emailed verification code and a new password. */
export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}
